import { Currency } from "@models/interfaces/currency";
import { CurrencyFilter } from "@models/interfaces/currency_filter";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Cryptocoin, Fiat } from "@models";
import { Metal } from "@models/asset/metal/metal";
import { Helper } from "@helpers/functions";
import { Tradable } from "@models/interfaces/tradable";
import { Index } from "@models/asset/index/index.model";
import { Stock } from "@models/derivative/currencies/stock";
import { Etf } from "@models/derivative/currencies/etf";
import { UserService } from "@main-services/user.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { Etc } from "@models/derivative/currencies/etc";
import { UntilDestroy } from "@ngneat/until-destroy";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { combineLatestWith, take } from "rxjs/operators";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { Observable } from "rxjs";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";

@UntilDestroy()
@Component({
	selector: "currency-filter",
	templateUrl: "./currency-filter.component.html",
	styleUrls: ["./currency-filter.component.scss"],
	standalone: false
})
export class CurrencyFilterComponent implements OnInit, CurrencyFilter {
	@Input()
	public filterType: typeof Currency = Currency;

	@Input() public set currencyTypes(types: (typeof Currency)[]) {
		if (this._currencyTypes.length !== types.length) {
			this._currencyTypes = types;
			this.initFilterOptions(); // Update filters on currencyTypeChange
		}
	}
	public get currencyTypes(): (typeof Currency)[] {
		return this._currencyTypes;
	}
	private _currencyTypes: (typeof Currency)[] = [
		Currency,
		Tradable,
		Metal,
		Cryptocoin,
		LeveragedToken,
		Index,
		Stock,
		Etf,
		Etc,
		SecurityToken
	];

	@Output()
	public onSelect: EventEmitter<typeof Currency> = new EventEmitter<typeof Currency>();

	@Input()
	public showNoResults: boolean = false;

	@Input()
	public keepVisibility: boolean = false; // Currency filters are hidden for unverified users. But we have exceptions like: /prices list where we need to show the filter.

	public currencyFilters: BpSelectOption[] = null;
	public isWalletDashboard: boolean = false;
	private areLeverageActive$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.LEVERAGE);
	private isSecTokensActive$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.SECURITY_TOKENS);
	private hideSteelCoin$ = this.statsigService.isFeatureActive(FEAT_GATES.REMOVE_STEELCOIN);

	private readonly defaultCurrencyFilters: BpSelectOption[] = [
		{ name: "currency_filter_view_all", value: Currency },
		{ name: "currency_filter_currencies", value: Fiat },
		{ name: "search_crypto", value: Cryptocoin },
		// Leverage tokens field should not be visible on UK website
		// { name: "currency_filter_leveraged_tokens", value: LeveragedToken },
		{ name: "currency_filter_crypto_indices", value: Index }
		// Security tokens field should not be visible on UK website
		// { name: "currency_filter_security", value: SecurityToken },
		// Stocks field should not be visible on UK website
		// { name: "currency_filter_stocks", value: Stock },
		// ETFs field should not be visible on UK website
		// { name: "currency_filter_etfs", value: Etf },
		// ETCs field should not be visible on UK website
		//{ name: "currency_filter_etcs", value: Etc }
		// Metals field should not be visible on UK website
		// { name: "currency_filter_metals", value: Metal }
	];

	public constructor(
		private userService: UserService,
		private launchDarklyService: LaunchDarklyService,
		private statsigService: StatsigClientService
	) {}

	public ngOnInit(): void {
		this.isWalletDashboard = this.currencyTypes.some((type: typeof Currency) => type === Fiat);
		this.initFilterOptions();
	}

	private initFilterOptions(): void {
		this.areLeverageActive$
			.pipe(combineLatestWith(this.isSecTokensActive$, this.hideSteelCoin$), take(1))
			.subscribe(([leverageActive, secTokensActive, hideSteelCoin]) => {
				this.currencyFilters = this.defaultCurrencyFilters; // Needs to be reset before sorting out filters again

				if (!leverageActive) {
					this.currencyFilters = this.defaultCurrencyFilters.filter(
						(c) => c.name !== "currency_filter_leveraged_tokens"
					);
				}

				if (!secTokensActive || hideSteelCoin) {
					this.currencyFilters = this.defaultCurrencyFilters.filter(
						(c) => c.name !== "currency_filter_security"
					);
				}
				this.setSelectedCurrency();
			});
	}

	private setSelectedCurrency(): void {
		this.currencyTypes = this.currencyTypes.filter((t) => this.isInheritOfFilter(t));
		this.currencyFilters = this.currencyFilters
			.filter((bp) => this.currencyTypes.some((t) => t === bp.value))
			.filter((bp) => this.isInheritOfFilter(bp.value));

		if (
			this.filterType !== Currency &&
			(this.currencyFilters.length <= 2 ||
				!this.currencyFilters.some((filter) => filter.value === this.filterType))
		) {
			this.select(Currency);
		} // Important to reset properly, in case the user has a filter selected which isn't present anymore!!
	}

	public select(t: typeof Currency): void {
		this.filterType = t;
		this.onSelect.emit(t);
	}

	public get isUserVerified(): boolean {
		return this.userService.user.isVerified();
	}

	public get isUserAtLeastDocumented(): boolean {
		return this.isUserVerified || this.userService.user.isDocumented();
	}

	private isInheritOfFilter(t: typeof Currency): boolean {
		return Helper.isInheritOf(t, this.filterType);
	}

	public showFilter(): boolean {
		return (
			(this.keepVisibility ||
				this.isUserAtLeastDocumented ||
				(!this.isUserAtLeastDocumented && this.isWalletDashboard)) &&
			this.currencyTypes.length > 2
		); // More than 2 currencytypes because one type will always be "all"
	}
}
